import { useRef, useState } from 'react'
import { useGameStore } from '@/store/gameStore'
import {
  SAVE_SLOTS,
  SaveSlot,
  listSlots,
  saveToSlot,
  loadSlot,
  deleteSlot,
  exportSave,
  importSave,
  getAutoSaveMetadata,
  loadAutoSave,
} from '@/systems/SaveSystem'
import { IconTile } from '../components/CourtVision'
import { CollapsibleSection } from '../components/CollapsibleSection'
import { AngledButton } from '../components/broadcast'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useConfirm } from '../components/useConfirm'
import { MusicLibrarySettings, VolumeSettings } from './AudioSettingsTab'
import { TimeControls } from '../components/TimeControls'
import { REGION_META } from '@/config/regionConfig'
import { GAME_MODES } from '@/config/gameModesConfig'
import { Save, FolderOpen, Trash2, Download, Upload, HardDrive, FileQuestion, Gauge, LogOut, Music, Volume2, Clock, History } from 'lucide-react'

function SaveManagement() {
  const day = useGameStore(s => s.day)
  const [slots, setSlots] = useState(listSlots)
  const [autoMeta, setAutoMeta] = useState(getAutoSaveMetadata)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { confirm, confirmDialog } = useConfirm()

  const refresh = () => {
    setSlots(listSlots())
    setAutoMeta(getAutoSaveMetadata())
  }

  const handleSave = async (slot: SaveSlot) => {
    const existing = slots[slot - 1]
    if (existing && !(await confirm({
      title: 'Sobrescribir guardado',
      icon: <Save size={20} />,
      message: <>Vas a sobrescribir la partida del día {existing.day} en la ranura {slot}. ¿Continuar?</>,
      confirmLabel: 'Sobrescribir',
    }))) return
    saveToSlot(slot)
    refresh()
  }

  const handleLoad = async (slot: SaveSlot) => {
    const meta = slots[slot - 1]
    if (!meta) return
    if (!(await confirm({
      title: 'Cargar partida',
      icon: <FolderOpen size={20} />,
      message: <>Vas a cargar la partida del día {meta.day}. Perderás el progreso no guardado (día actual: {day}). ¿Continuar?</>,
      confirmLabel: 'Cargar',
    }))) return
    if (loadSlot(slot)) window.location.reload()
  }

  const handleLoadAuto = async () => {
    if (!autoMeta) return
    if (!(await confirm({
      title: 'Cargar auto-guardado',
      icon: <History size={20} />,
      message: <>Vas a cargar el auto-guardado del día {autoMeta.day}. Perderás el progreso no guardado (día actual: {day}). ¿Continuar?</>,
      confirmLabel: 'Cargar',
    }))) return
    if (loadAutoSave()) window.location.reload()
  }

  const handleDelete = async (slot: SaveSlot) => {
    const meta = slots[slot - 1]
    if (!meta) return
    if (!(await confirm({
      title: 'Borrar guardado',
      icon: <Trash2 size={20} />,
      message: <>Vas a borrar la partida del día {meta.day}. Esta acción no se puede deshacer.</>,
      confirmLabel: 'Borrar',
      confirmTone: 'danger',
    }))) return
    deleteSlot(slot)
    refresh()
  }

  const handleExport = () => {
    const json = exportSave()
    if (!json) return
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `padel-manager-dia${day}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportFile = async (file: File) => {
    const text = await file.text()
    const error = importSave(text)
    if (error) {
      await confirm({
        title: 'No se pudo importar',
        message: error,
        confirmLabel: 'Entendido',
        hideCancel: true,
      })
      return
    }
    window.location.reload()
  }

  const usedSlots = slots.filter(Boolean).length

  return (
    <CollapsibleSection title="Partidas guardadas" icon={HardDrive} meta={`${usedSlots}/${SAVE_SLOTS.length} ocupadas`}>
      <div className="space-y-2">
        {/* Auto-save: written automatically while playing; load-only here */}
        <div className="row-tile" style={!autoMeta ? { opacity: 0.7 } : undefined}>
          <IconTile icon={History} tone={autoMeta ? 'accent' : 'muted'} />
          <div className="row-tile-main">
            <div className="row-tile-title">Auto-guardado</div>
            <div className="row-tile-sub">
              {autoMeta
                ? `Dia ${autoMeta.day} · ${autoMeta.money.toLocaleString('es-ES')}€ · Nv.${autoMeta.clubLevel} · ${new Date(autoMeta.savedAt).toLocaleString('es-ES')}`
                : 'Se guarda solo mientras juegas'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
            {autoMeta && (
              <button onClick={handleLoadAuto} title="Cargar auto-guardado" className="icon-btn" style={{ color: 'var(--gold)' }}>
                <FolderOpen size={15} />
              </button>
            )}
          </div>
        </div>

        {SAVE_SLOTS.map(slot => {
          const meta = slots[slot - 1]
          return (
            <div key={slot} className="row-tile">
              <IconTile icon={meta ? Save : FileQuestion} tone={meta ? 'accent' : 'muted'} />
              <div className="row-tile-main">
                <div className="row-tile-title">Ranura {slot}</div>
                <div className="row-tile-sub">
                  {meta
                    ? `Dia ${meta.day} · ${meta.money.toLocaleString('es-ES')}€ · Nv.${meta.clubLevel} · ${new Date(meta.savedAt).toLocaleString('es-ES')}`
                    : 'Vacia — un hueco esperando tu próxima gesta'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                <button onClick={() => handleSave(slot)} title="Guardar aqui" className="icon-btn" style={{ color: 'var(--success)' }}>
                  <Save size={15} />
                </button>
                {meta && (
                  <>
                    <button onClick={() => handleLoad(slot)} title="Cargar" className="icon-btn" style={{ color: 'var(--gold)' }}>
                      <FolderOpen size={15} />
                    </button>
                    <button onClick={() => handleDelete(slot)} title="Borrar" className="icon-btn" style={{ color: 'var(--danger)' }}>
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '10px', flexWrap: 'wrap' }}>
        <AngledButton tone="gold" onClick={handleExport}>
          <Download size={14} /> Exportar partida
        </AngledButton>
        <AngledButton tone="neutral" onClick={() => fileInputRef.current?.click()}>
          <Upload size={14} /> Importar partida
        </AngledButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) handleImportFile(file)
            e.target.value = ''
          }}
        />
      </div>

      <div className="hint-box" style={{ marginTop: '12px' }}>
        El juego se auto-guarda mientras juegas (arriba del todo, y puedes
        cargarlo). Las ranuras son copias manuales; exportar descarga la
        partida como archivo JSON.
      </div>

      {confirmDialog}
    </CollapsibleSection>
  )
}

function GameInfo() {
  const region = useGameStore(s => s.region)
  const gameMode = useGameStore(s => s.gameMode)
  const managerClubName = useGameStore(s => s.managerClubName)
  const modeLabel = GAME_MODES.find(m => m.id === gameMode)?.label ?? 'Carrera del Director'
  const regionMeta = REGION_META[region]

  return (
    <CollapsibleSection title="Info de la partida" icon={Gauge}>
      <div className="row-tile" style={{ alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <IconTile icon={Gauge} tone="warning" />
        <div className="row-tile-main">
          <div className="row-tile-title">{managerClubName || 'Mi Club'}</div>
          <div className="row-tile-sub">{modeLabel}</div>
        </div>
        <span className="chip-angled tone-gold">{regionMeta?.label ?? 'Costa del Sol'}</span>
      </div>
    </CollapsibleSection>
  )
}

function SessionControls() {
  const setPlaying = useGameStore(s => s.setPlaying)
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <CollapsibleSection title="Salir de la partida" icon={LogOut}>
      <div className="hint-box" style={{ marginBottom: '10px' }}>
        Vuelve al menú principal para empezar una partida nueva o cargar otra.
        Tu partida actual se guarda automáticamente; "Continuar" la retoma donde
        la dejaste. (Para conservar una copia aparte, guárdala antes en una ranura.)
      </div>
      <AngledButton tone="neutral" onClick={() => setConfirmOpen(true)}>
        <LogOut size={14} /> Salir al menú principal
      </AngledButton>

      <ConfirmDialog
        open={confirmOpen}
        icon={<LogOut size={20} />}
        title="Salir al menú principal"
        message={<>Tu partida se guarda automáticamente: podrás retomarla con <b>«Continuar»</b> o empezar una nueva. ¿Salir ahora?</>}
        confirmLabel="Sí, salir"
        cancelLabel="Cancelar"
        onConfirm={() => { setConfirmOpen(false); setPlaying(false) }}
        onCancel={() => setConfirmOpen(false)}
      />
    </CollapsibleSection>
  )
}

function GameSpeedSettings() {
  return (
    <CollapsibleSection title="Velocidad del juego" icon={Clock}>
      <div className="hint-box" style={{ marginBottom: '12px' }}>
        Pausa la simulación o ajusta su ritmo (1x · 2x · 4x).
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 6px' }}>
        <TimeControls />
      </div>
    </CollapsibleSection>
  )
}

// The theme picker is hidden while dark mode is forced (FORCE_DARK in
// themeStore.ts); restore it from git history when the light theme returns.
export function SettingsTabContent() {
  return (
    <div className="settings-content" style={{ padding: '10px' }}>
      <div className="settings-group-title">Partida</div>
      <GameInfo />
      <GameSpeedSettings />
      <SaveManagement />
      <SessionControls />

      <div className="settings-group-title">Audio</div>
      <CollapsibleSection title="Música" icon={Music}>
        <MusicLibrarySettings />
      </CollapsibleSection>
      <CollapsibleSection title="Volumen y efectos" icon={Volume2}>
        <VolumeSettings />
      </CollapsibleSection>
    </div>
  )
}
